/**
 * @fileoverview Which names `capture()` may use.
 * @module features/analytics/captureNames
 *
 * `capture()` stores the customer's own name as the event's name, so it shares
 * one namespace with the core events the SDK sends itself. A captured
 * `click_event` would be counted as Flowgrid's click stream; a captured
 * `signup_completed` would inflate sign-ups. Only those names are refused.
 *
 * Exactly the core wire names — not the dotted enterprise RPCs
 * (`alerts.list`, `cohorts.create`, …), which are calls, not events.
 * `tests/capture.test.ts` scans the source so this list can't drift.
 */

export const RESERVED_EVENT_NAMES: ReadonlySet<string> = new Set<string>([
  "active_user", "alias_user", "analyze_funnel", "api_timing", "attribution_conversion", "attribution_touchpoint",
  "batch_events", "click_event", "compare_funnel", "create_tracking", "custom_event", "define_funnel", "error_event", "first_action",
  "form_event", "funnel_abandon", "funnel_complete", "funnel_step", "group_user",
  "heatmap_click", "heatmap_movement", "heatmap_rage_click", "heatmap_scroll",
  "identify_user", "lifecycle_change", "list_funnels", "login_completed", "onboarding_completed", "onboarding_step",
  "page_exit", "page_load", "page_view", "prompt_conversation", "prompt_feedback", "prompt_response", "prompt_submitted",
  "reset_identity", "resource_timing", "scroll_depth", "search_event",
  "session_end", "session_heartbeat", "session_start", "session_update", "signup_completed",
  "subscription_cancel", "subscription_change", "subscription_renew", "subscription_start",
  "time_on_page", "trial_converted", "trial_expired", "update_traits", "user_activity", "web_vitals",
]);

export const MAX_CAPTURE_NAME_LENGTH = 100;

export type CaptureNameCheck =
  | { ok: true; name: string }
  | { ok: false; reason: string };

/**
 * Validate a `capture()` name. Exact names are kept — `Checkout Opened` and
 * `checkout_opened` are two events, the same as in the dashboard.
 */
export function checkCaptureName(raw: unknown): CaptureNameCheck {
  if (typeof raw !== "string" || raw.trim() === "") {
    return { ok: false, reason: "the event name must be a non-empty string" };
  }

  const name = raw.trim();
  if (name.length > MAX_CAPTURE_NAME_LENGTH) {
    return { ok: false, reason: `"${name.slice(0, 40)}…" is longer than ${MAX_CAPTURE_NAME_LENGTH} characters` };
  }

  if (RESERVED_EVENT_NAMES.has(name)) {
    return {
      ok: false,
      reason: `"${name}" is a name Flowgrid sends itself — use track("${name}") for it, or pick your own name for capture()`,
    };
  }

  return { ok: true, name };
}
